import type { AuthUser, JwtPayload, UserRole } from "@/types";
import { USER_ROLES } from "@/constants/roles";

const isUserRole = (value: unknown): value is UserRole => {
  return (
    typeof value === "string" &&
    (USER_ROLES as readonly string[]).includes(value)
  );
};

/**
 * Decodes JWT payload without signature verification (client-side only)
 */
export const decodeJwtPayload = (token: string): JwtPayload | null => {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const parsed = JSON.parse(decoded) as Record<string, unknown>;

    return {
      sub: String(parsed.sub ?? ""),
      email: String(parsed.email ?? ""),
      role: isUserRole(parsed.role) ? parsed.role : undefined,
      tenantId:
        typeof parsed.tenantId === "string" ? parsed.tenantId : undefined,
      tenantSlug:
        typeof parsed.tenantSlug === "string" ? parsed.tenantSlug : undefined,
      iat: typeof parsed.iat === "number" ? parsed.iat : undefined,
      exp: typeof parsed.exp === "number" ? parsed.exp : undefined,
    };
  } catch {
    return null;
  }
};

/**
 * Maps JWT payload to AuthUser
 */
export const payloadToAuthUser = (payload: JwtPayload): AuthUser | null => {
  if (!payload.sub || !payload.email || !payload.role) {
    return null;
  }

  return {
    id: payload.sub,
    email: payload.email,
    role: payload.role,
    tenantId: payload.tenantId,
    tenantSlug: payload.tenantSlug,
  };
};
